import { ChevronRight, Flame } from "lucide-react";
import { useSite, waLink, resolveImg } from "@/lib/content";

function BgWord({ children, className = "" }) {
  return (
    <div
      aria-hidden="true"
      data-testid="bg-word"
      className={`pointer-events-none select-none absolute inset-x-0 font-serif-display italic leading-[0.85] whitespace-nowrap text-center ${className}`}
    >
      {children}
    </div>
  );
}

export default function Accueil() {
  const { content: c } = useSite();
  const whatsapp = waLink(c);
  const menuUrl = resolveImg(c.menu_pdf_url || "/menu-la-matze.pdf");

  const specialties = [
    { num: "01", name: c.specialty_0_name, image: c.specialty_0_image, span: "md:col-span-7 md:row-span-2 md:aspect-auto aspect-[4/5]" },
    { num: "02", name: c.specialty_1_name, image: c.specialty_1_image, span: "md:col-span-5 aspect-[4/3]" },
    { num: "03", name: c.specialty_2_name, image: c.specialty_2_image, span: "md:col-span-5 aspect-[4/3]" },
  ];

  return (
    <div data-testid="page-accueil">
      {/* HERO */}
      <section className="relative min-h-[85vh] sm:min-h-[92vh] w-full overflow-hidden lm-grain">
        <img src={c.hero_image} alt="La devanture du restaurant La Matze à Genève" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#1A1A18]/65 via-[#1A1A18]/50 to-[#1A1A18]/90" />
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 pt-28 sm:pt-40 pb-14 sm:pb-32 min-h-[85vh] sm:min-h-[92vh] flex flex-col justify-end">
          <div className="max-w-3xl">
            <span className="lm-eyebrow lm-fade-up text-[10px] sm:text-[12px]" style={{ color: "#E8B0B6" }}>
              {c.hero_eyebrow}
            </span>
            <h1 className="lm-fade-up lm-delay-1 font-serif-display text-white mt-5 sm:mt-8 text-[2.5rem] sm:text-6xl lg:text-7xl leading-[1] sm:leading-[0.95] tracking-tight">
              {c.hero_title_1}<br />
              <em className="text-[#F3D1D6] font-normal italic">{c.hero_title_2}</em><br />
              {c.hero_title_3}
            </h1>
            <p className="lm-fade-up lm-delay-2 mt-5 sm:mt-8 max-w-xl text-white/85 text-[14px] sm:text-[17px] leading-relaxed">
              {c.hero_paragraph}
            </p>
            <div className="lm-fade-up lm-delay-3 mt-7 sm:mt-12 flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4">
              <a href={whatsapp} target="_blank" rel="noreferrer noopener" data-testid="hero-cta-reserver" className="lm-btn-primary justify-center sm:justify-start">
                Réserver via WhatsApp <ChevronRight size={16} />
              </a>
              <a href={menuUrl} target="_blank" rel="noreferrer noopener" data-testid="hero-cta-menu" className="lm-btn-outline justify-center sm:justify-start" style={{ color: "#FDFBF7", borderColor: "#FDFBF7" }}>
                Voir le menu
              </a>
            </div>
          </div>
        </div>
        <div className="absolute right-10 top-32 hidden lg:block text-right z-10 text-white/85">
          <div className="text-[11px] tracking-[0.32em] uppercase text-[#F3D1D6]">Adresse</div>
          <div className="font-serif-display text-lg mt-2">{c.address.split(",")[0]}</div>
          <div className="text-sm text-white/70">{(c.address.split(",")[1] || "").trim()}</div>
        </div>
      </section>

      {/* INTRO */}
      <section className="relative overflow-hidden">
        <BgWord className="top-1/2 -translate-y-1/2 text-[28vw] sm:text-[22vw] text-[#8A1C29]/[0.05]">Maison</BgWord>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 py-16 sm:py-28 lg:py-40 grid lg:grid-cols-12 gap-8 lg:gap-14">
          <div className="lg:col-span-5">
            <span className="lm-eyebrow">{c.intro_eyebrow}</span>
            <h2 className="font-serif-display text-[1.75rem] sm:text-4xl lg:text-5xl leading-tight mt-5 sm:mt-6 text-[#1A1A18]">
              {c.intro_title_1}<br />
              {c.intro_title_2} <em className="text-[#8A1C29] italic">{c.intro_title_italic}</em>.
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 text-[#5C544F] text-[14px] sm:text-[17px] leading-[1.75] mt-2 lg:mt-0">
            <p>{c.intro_paragraph}</p>
            <p className="mt-5 sm:mt-6 text-[13px] sm:text-[15px] whitespace-pre-line">{c.intro_hours}</p>
          </div>
        </div>
      </section>

      {/* SPECIALTIES */}
      <section className="relative bg-[#F3EFE9] py-16 sm:py-28 lg:py-36 border-y border-[#E8E2D9] overflow-hidden">
        <BgWord className="top-8 sm:top-12 text-[24vw] sm:text-[18vw] text-[#8A1C29]/[0.055]">Carte</BgWord>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 lg:gap-8 mb-10 lg:mb-20">
            <div>
              <span className="lm-eyebrow">Les incontournables</span>
              <h2 className="font-serif-display text-[1.75rem] sm:text-4xl lg:text-6xl mt-5 sm:mt-6 text-[#1A1A18] leading-[1.1] max-w-3xl">
                Quatre plats qui font <em className="italic text-[#8A1C29]">la maison</em>.
              </h2>
            </div>
            <a href={menuUrl} target="_blank" rel="noreferrer noopener" data-testid="specialties-cta-menu" className="text-[#8A1C29] uppercase tracking-[0.24em] text-[11px] sm:text-[12px] font-medium inline-flex items-center gap-2 hover:gap-3 transition-all self-start">
              Voir toute la carte <ChevronRight size={14} />
            </a>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
            {specialties.map((s, i) => (
              <article key={s.num} data-testid={`specialty-${i}`} className={`group relative overflow-hidden bg-[#1A1A18] ${s.span}`}>
                <img src={s.image} alt={s.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-95 group-hover:opacity-100 group-hover:scale-105 transition-all duration-[1400ms] ease-out" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A18]/50 via-transparent to-transparent" />
                <div className="relative h-full flex flex-col p-5 sm:p-8 lg:p-10 text-[#FDFBF7] min-h-[280px] sm:min-h-[320px]">
                  <span className="font-serif-display italic text-3xl sm:text-5xl text-[#F3D1D6]/90 leading-none">{s.num}</span>
                </div>
              </article>
            ))}

            <article data-testid="specialty-3" className="md:col-span-12 grid grid-cols-1 md:grid-cols-2 border border-[#E8E2D9] bg-[#FDFBF7] overflow-hidden">
              <div className="p-6 sm:p-10 lg:p-14 flex flex-col justify-between order-2 md:order-1">
                <div className="flex items-start justify-between gap-3">
                  <span className="font-serif-display italic text-3xl sm:text-5xl text-[#8A1C29]/50 leading-none">04</span>
                  <Flame size={14} className="text-[#8A1C29] mt-2 sm:mt-3" />
                </div>
                <div className="mt-6 sm:mt-10">
                  <h3 className="font-serif-display text-2xl sm:text-4xl lg:text-5xl text-[#1A1A18] leading-[1.05]">{c.specialty_3_name}</h3>
                </div>
              </div>
              <div className="order-1 md:order-2 aspect-[4/3] md:aspect-auto md:min-h-[380px] relative overflow-hidden">
                <img src={c.specialty_3_image} alt={c.specialty_3_name} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden">
        <BgWord className="top-1/2 -translate-y-1/2 text-[28vw] sm:text-[20vw] text-[#8A1C29]/[0.05]">Bienvenue</BgWord>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 py-16 sm:py-28 lg:py-36 text-center">
          <span className="lm-eyebrow justify-center">Envie de nous rejoindre&nbsp;?</span>
          <h2 className="font-serif-display text-[1.75rem] sm:text-5xl lg:text-6xl mt-5 sm:mt-8 leading-tight text-[#1A1A18] max-w-3xl mx-auto">
            Réservez votre table <em className="italic text-[#8A1C29]">par WhatsApp</em>.
          </h2>
          <p className="mt-5 sm:mt-6 max-w-xl mx-auto text-[#5C544F] text-[14px] sm:text-[16px] leading-relaxed">
            Un message, une réponse — souvent dans l&rsquo;heure. Ouvert du lundi au samedi, 10h – 22h.
          </p>
          <div className="mt-8 sm:mt-12 flex flex-col sm:flex-row flex-wrap justify-center gap-3 sm:gap-4">
            <a href={whatsapp} target="_blank" rel="noreferrer noopener" data-testid="final-cta-reserver" className="lm-btn-primary justify-center">Réserver via WhatsApp</a>
            <a href={`tel:${c.phone_tel}`} data-testid="final-cta-phone" className="lm-btn-outline justify-center">{c.phone_display}</a>
          </div>
        </div>
      </section>
    </div>
  );
}
